import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Header } from '@/components/layout/header/Header';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import costsService from './costsService';
import CostsFilters from './CostsFilters';
import CostsTypeFilter from './CostsTypeFilter';
import CostsDashboard from './CostsDashboard';
import { format } from 'date-fns';
import CostsTable from './CostsTable';
import CostsForm from './CostsForm';
import type { CostTotals, TrendItem, PaginatedCosts, CostItem } from './costsService';

function getDefaultRange() {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: format(firstDay, 'yyyy-MM-dd'),
    end: format(lastDay, 'yyyy-MM-dd'),
  };
}

export default function CostsPage() {
  useLanguage();
  const { user } = useCustomAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.nivel !== 'admin') {
      toast.error(translateInterfaceText("Área restrita a administradores."));
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const [startDate, setStartDate] = useState(getDefaultRange().start);
  const [endDate, setEndDate] = useState(getDefaultRange().end);

  const [totals, setTotals] = useState<CostTotals | null>(null);
  const [trend, setTrend] = useState<TrendItem[]>([]);
  const [costsList, setCostsList] = useState<PaginatedCosts | null>(null);
  const [manualItems, setManualItems] = useState<CostItem[]>([]);
  const [allItems, setAllItems] = useState<CostItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [loadingTotals, setLoadingTotals] = useState(false);
  const [loadingTrend, setLoadingTrend] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingManual, setLoadingManual] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<CostItem | null>(null);
  const [selectedType, setSelectedType] = useState('todos');

  const uniqueProviders = useMemo(() => {
    const providers = new Set<string>();
    allItems.forEach((item) => providers.add(item.provider));
    return Array.from(providers);
  }, [allItems]);

  const filteredTotals = useMemo(() => {
    if (!totals) return null;
    if (selectedType === 'todos') return totals;

    const filtered = allItems.filter((item) => item.provider === selectedType);

    const byProvider = new Map<string, { totalUSD: number; totalBRL: number; estimatedTaxBRL: number; totalWithTaxBRL: number }>();
    const byCategory = new Map<string, { totalUSD: number; totalBRL: number; estimatedTaxBRL: number; totalWithTaxBRL: number }>();
    const byService = new Map<string, { totalUSD: number; totalBRL: number; estimatedTaxBRL: number; totalWithTaxBRL: number }>();

    let totalUSD = 0;
    let totalBRL = 0;
    let estimatedTaxBRL = 0;
    let totalWithTaxBRL = 0;

    for (const item of filtered) {
      totalUSD += item.currencyOriginal === 'USD' ? item.amountOriginal : 0;
      totalBRL += item.amountBRL;
      estimatedTaxBRL += item.estimatedTaxBRL;
      totalWithTaxBRL += item.totalWithTaxBRL;

      const providerData = byProvider.get(item.provider) || { totalUSD: 0, totalBRL: 0, estimatedTaxBRL: 0, totalWithTaxBRL: 0 };
      providerData.totalUSD += item.currencyOriginal === 'USD' ? item.amountOriginal : 0;
      providerData.totalBRL += item.amountBRL;
      providerData.estimatedTaxBRL += item.estimatedTaxBRL;
      providerData.totalWithTaxBRL += item.totalWithTaxBRL;
      byProvider.set(item.provider, providerData);

      const categoryData = byCategory.get(item.category) || { totalUSD: 0, totalBRL: 0, estimatedTaxBRL: 0, totalWithTaxBRL: 0 };
      categoryData.totalUSD += item.currencyOriginal === 'USD' ? item.amountOriginal : 0;
      categoryData.totalBRL += item.amountBRL;
      categoryData.estimatedTaxBRL += item.estimatedTaxBRL;
      categoryData.totalWithTaxBRL += item.totalWithTaxBRL;
      byCategory.set(item.category, categoryData);

      const serviceData = byService.get(item.service_name) || { totalUSD: 0, totalBRL: 0, estimatedTaxBRL: 0, totalWithTaxBRL: 0 };
      serviceData.totalUSD += item.currencyOriginal === 'USD' ? item.amountOriginal : 0;
      serviceData.totalBRL += item.amountBRL;
      serviceData.estimatedTaxBRL += item.estimatedTaxBRL;
      serviceData.totalWithTaxBRL += item.totalWithTaxBRL;
      byService.set(item.service_name, serviceData);
    }

    return {
      ...totals,
      totalUSD,
      totalBRL,
      estimatedTaxBRL,
      totalWithTaxBRL,
      totalBRLDirect: totalBRL,
      totalBRLConverted: totalBRL,
      byProvider: Array.from(byProvider.entries()).map(([key, value]) => ({ _id: key, ...value })),
      byCategory: Array.from(byCategory.entries()).map(([key, value]) => ({ _id: key, ...value })),
      byService: Array.from(byService.entries()).map(([key, value]) => ({ _id: key, ...value })),
    };
  }, [totals, selectedType, allItems]);

  const fetchAll = useCallback(async (start: string, end: string, p: number = 1) => {
    setLoadingTotals(true);
    setLoadingTrend(true);
    setLoadingList(true);
    setLoadingManual(true);
    try {
      const [totalsRes, trendRes, listRes, manualRes, allItemsRes] = await Promise.all([
        costsService.getTotals(start, end).catch(() => null),
        costsService.getTrend(start, end).catch(() => ({ data: [] })),
        costsService.getList(start, end, p, 20).catch(() => ({ data: [], total: 0, page: 1, limit: 20, totalPages: 1 })),
        costsService.getManualItems(start, end).catch(() => []),
        costsService.getAllItems(start, end).catch(() => []),
      ]);
      setTotals(totalsRes);
      setManualItems(manualRes || []);
      setAllItems(allItemsRes || []);

      // Se a API de trend não retornou dados, busca TODOS os itens para gerar trend completo
      let trendData = trendRes?.data || [];
      if (trendData.length === 0) {
        const allItems = allItemsRes || [];
        if (allItems.length > 0) {
          trendData = costsService.buildTrendFromList(allItems);
        }
      }
      setTrend(trendData);

      setCostsList(listRes || null);
      setPage(listRes?.page || 1);
      setTotalPages(listRes?.totalPages || 1);
    } finally {
      setLoadingTotals(false);
      setLoadingTrend(false);
      setLoadingList(false);
      setLoadingManual(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(startDate, endDate);
  }, []);

  const handleApply = () => {
    setPage(1);
    fetchAll(startDate, endDate, 1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchAll(startDate, endDate, newPage);
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await costsService.syncAWS(startDate, endDate);
      toast.success(translateInterfaceText("Sincronização com AWS concluída!"));
      fetchAll(startDate, endDate, page);
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao sincronizar com AWS."));
    } finally {
      setSyncing(false);
    }
  };

  const handleEdit = (item: CostItem) => {
    setEditItem(item);
    setFormOpen(true);
  };

  const handleDelete = async (item: CostItem) => {
    if (!window.confirm(`Excluir custo "${item.service_name}"?`)) return;
    try {
      await costsService.remove(item.id_cost);
      toast.success(translateInterfaceText("Custo excluído!"));
      fetchAll(startDate, endDate, page);
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao excluir."));
    }
  };

  const handleDeactivateRecurring = async (item: CostItem) => {
    if (!window.confirm(`Desativar recorrência de "${item.service_name}"?\n\nAs instâncias futuras não serão mais criadas automaticamente.`)) return;
    try {
      await costsService.deactivateRecurring(item.id_cost);
      toast.success(translateInterfaceText("Recorrência desativada com sucesso!"));
      fetchAll(startDate, endDate, page);
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao desativar recorrência."));
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditItem(null);
  };

  const handleSaved = () => {
    fetchAll(startDate, endDate, page);
  };

  if (!user || user.nivel !== 'admin') return null;

  return (
    <Layout fixedSidebar={true}>
      <div className="flex-1 flex flex-col mobile-scroll-fix">
        <Header title={translateInterfaceText("Gestão de Custos")} />
        <div className="hidden lg:block h-px bg-border w-full" />
        <main className="flex-1 lg:overflow-y-auto">
          <div className="container mx-auto px-4 md:px-6 py-6 md:py-8 pb-[calc(120px+env(safe-area-inset-bottom))] lg:pb-28 space-y-6 max-w-6xl">

            {/* BANNER */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
              <div>
                <h2 className="text-md font-bold text-slate-900">{translateInterfaceText("Custos de Infraestrutura")}</h2>
                <p className="text-xs text-slate-400 mt-0.5">{translateInterfaceText("Dashboard financeiro com totais, gráficos e listagem de custos AWS + despesas manuais.")}</p>
              </div>
              <button
                onClick={() => { setEditItem(null); setFormOpen(true); }}
                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-95 shrink-0"
              >
                <Plus size={15} /> {" "}{translateInterfaceText("Nova Despesa")}</button>
            </div>

            {/* FILTERS */}
            <CostsFilters
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onApply={handleApply}
              onSync={handleSync}
              syncing={syncing}
            />

            {/* TYPE FILTER */}
            <CostsTypeFilter
              providers={uniqueProviders}
              selectedType={selectedType}
              onTypeChange={setSelectedType}
            />

            {/* DASHBOARD */}
            <CostsDashboard totals={filteredTotals} trend={trend} loading={loadingTotals || loadingTrend} selectedType={selectedType} />

            {/* TABLE */}
            <CostsTable
              data={costsList}
              manualItems={manualItems}
              allItems={allItems}
              loading={loadingList}
              loadingManual={loadingManual}
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDeactivateRecurring={handleDeactivateRecurring}
              selectedType={selectedType}
            />

            {/* FORM MODAL */}
            <CostsForm
              isOpen={formOpen}
              onClose={handleFormClose}
              editItem={editItem}
              onSaved={handleSaved}
            />
          </div>
        </main>
      </div>
    </Layout>
  );
}
